import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, finalize } from 'rxjs';
import { AppFacadeService } from '../../../services/app-facade.service';
import { CustomerService } from '../../../services/customer.service';
import { ModalService } from '../../../services/modal.service';
import { NotificationService } from '../../../services/notification.service';

export const ADD_CONTACT_MODAL_ID = 'add-contact-modal';

type AddContactForm = {
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    email: FormControl<string>;
    phone: FormControl<string>;
};

@Component({
    standalone: false,
    selector: 'por-add-contact-modal',
    templateUrl: './add-contact-modal.component.html',
    styleUrls: ['./add-contact-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddContactModalComponent implements OnInit, OnDestroy {
    private readonly fb = inject(FormBuilder);
    private readonly customerService = inject(CustomerService);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly modalService = inject(ModalService);
    private readonly notificationService = inject(NotificationService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);

    @Input() customerId!: string;

    readonly modalId = ADD_CONTACT_MODAL_ID;

    contactForm!: FormGroup<AddContactForm>;
    isSaving = false;

    private readonly subscriptions: Subscription[] = [];

    get firstName() {
        return this.contactForm.get('firstName');
    }

    get lastName() {
        return this.contactForm.get('lastName');
    }

    get email() {
        return this.contactForm.get('email');
    }

    ngOnInit(): void {
        this.contactForm = this.fb.group<AddContactForm>({
            firstName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
            lastName: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
            email: new FormControl<string>('', { nonNullable: true, validators: [Validators.email] }),
            phone: new FormControl<string>('', { nonNullable: true })
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    reset(): void {
        this.contactForm.reset();
        this.isSaving = false;
        this.cdr.markForCheck();
    }

    onSave(): void {
        if (!this.customerId || this.contactForm.invalid || this.isSaving) {
            return;
        }

        this.isSaving = true;
        this.cdr.markForCheck();

        this.subscriptions.push(
            this.customerService
                .createAccountContact(this.customerId, this.contactForm.getRawValue())
                .pipe(
                    finalize(() => {
                        this.isSaving = false;
                        this.cdr.markForCheck();
                    })
                )
                .subscribe({
                    next: () => {
                        this.notificationService.success(this.translateService.instant('AddContact.SaveSuccess'));
                        this.appFacadeService.loadCustomerContacts(this.customerId);
                        this.modalService.close(this.modalId);
                        this.reset();
                    },
                    error: () => {
                        this.notificationService.error(this.translateService.instant('AddContact.SaveError'));
                    }
                })
        );
    }

    onCancel(): void {
        this.modalService.close(this.modalId);
        this.reset();
    }
}
